import { NormalizedUnitNumber } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'

import { MobileViewOptions } from '../types'

interface CompactValueCellProps {
  token: Token
  value: NormalizedUnitNumber
  compactValue?: boolean
  dimmed?: boolean
  hideEmpty?: boolean
  mobileViewOptions?: MobileViewOptions
  className?: string
  'data-testid'?: string
  withSymbol?: boolean
}

export function CompactValueCell({
  value,
  token,
  compactValue,
  dimmed,
  hideEmpty = false,
  mobileViewOptions,
  withSymbol = false,
  className,
  'data-testid': dataTestId,
}: CompactValueCellProps) {
  if (mobileViewOptions?.isMobileView) {
    return (
      <div
        className={cn(
          'flex flex-row items-center',
          mobileViewOptions.showOnMobile ? 'justify-center' : 'justify-between',
        )}
      >
        {!mobileViewOptions.showOnMobile && (
          <Typography variant="prompt" className="w-full">
            {mobileViewOptions.rowTitle}
          </Typography>
        )}
        <CompactValue
          token={token}
          value={value}
          dimmed={dimmed}
          hideEmpty={hideEmpty}
          compactValue={compactValue}
          className={className}
          data-testid={dataTestId}
          mobileViewOptions={mobileViewOptions}
          withSymbol={withSymbol}
        />
      </div>
    )
  }

  return (
    <CompactValue
      token={token}
      value={value}
      dimmed={dimmed}
      hideEmpty={hideEmpty}
      compactValue={compactValue}
      className={className}
      data-testid={dataTestId}
      withSymbol={withSymbol}
    />
  )
}

interface CompactValueProps {
  token: Token
  value: NormalizedUnitNumber
  compactValue?: boolean
  dimmed?: boolean
  hideEmpty?: boolean
  className?: string
  mobileViewOptions?: MobileViewOptions
  'data-testid'?: string
  withSymbol?: boolean
}

function CompactValue({
  token,
  value,
  compactValue,
  dimmed,
  hideEmpty,
  className,
  withSymbol,
  mobileViewOptions,
  'data-testid': dataTestId,
}: CompactValueProps) {
  if (hideEmpty && value.isZero()) {
    return (
      <div
        className={cn(
          'flex w-full flex-row',
          dimmed && 'text-muted-foreground',
          mobileViewOptions?.isMobileView && mobileViewOptions.showOnMobile ? 'justify-center' : 'justify-end',
        )}
        data-testid={dataTestId}
      >
        —
      </div>
    )
  }
  return (
    <div className={cn('flex flex-col', className)} data-testid={dataTestId}>
      <div className={cn('flex w-full flex-row justify-end gap-1', dimmed && 'text-muted-foreground')}>
        {token.format(value, { style: compactValue ? 'compact' : 'auto' })}
        {withSymbol && <span>{token.symbol}</span>}
      </div>
      <div className="flex w-full flex-row justify-end">
        <Typography variant="prompt" className={cn(dimmed && 'text-muted-foreground')}>
          {token.formatUSD(value, { compact: compactValue })}
        </Typography>
      </div>
    </div>
  )
}
